/**
 * FeedbackManager - Visual feedback system for molecular elements
 * Copied from svg-lib and adapted for svg-interactive
 *
 * Provides visual feedback states (hover, selected, etc.) with Framer Motion integration.
 * Works with both static and motion rendering paths.
 */

import React, { useMemo } from 'react';

// Type for framer-motion animation variants
type Variants = Record<string, any>;

export type FeedbackState =
  | 'idle'
  | 'hover'
  | 'partial-hover'
  | 'selected'
  | 'selected-hover'
  | 'anchor-orbit-hover'
  | 'anchor-orbit-dragging'
  | 'anchor-orbit-orbiting'
  | 'disabled'
  | 'error'
  | 'success'
  | 'preview'
  | 'ghost';

export interface FeedbackStyle {
  stroke?: string;
  strokeWidth?: number;
  strokeDasharray?: string;
  fill?: string;
  fillOpacity?: number;
  filter?: string;
  scale?: number;
  rotate?: number;
  opacity?: number;
  cursor?: string;
  transition?: any;

  // Hitbox-specific properties (migrated from HitboxConfig)
  hitboxStroke?: string;
  // Atom hitbox properties
  atomRadius?: number;           // Radius for atom hitboxes
  atomColor?: string;            // Color for atom hitboxes
  atomStroke?: string;           // Stroke color for atom hitboxes
  atomStrokeWidth?: number;      // Stroke width for atom hitboxes

  // Bond hitbox properties
  bondThickness?: number;        // Thickness for bond hitboxes
  bondColor?: string;            // Color for bond hitboxes
  bondStroke?: string;           // Stroke color for bond hitboxes
  bondStrokeWidth?: number;      // Stroke width for bond hitboxes
  isBondRounded?: boolean;       // Whether bond hitboxes have rounded corners (default: true)

  // Lone pair hitbox properties
  lonePairColor?: string;        // Color for lone pair hitboxes and visual dots
  lonePairStroke?: string;       // Stroke color for lone pair hitboxes
  lonePairStrokeWidth?: number;  // Stroke width for lone pair hitboxes

  // Arrow hitbox properties
  arrowStrokeMultiplier?: number; // Stroke width multiplier for arrow highlights
  arrowColor?: string;            // Color for arrow hitboxes

  // Legacy lone pair/radical hitbox properties
  hitboxRadius?: number;         // For radicals: circle radius
  hitboxRx?: number;             // For lone pairs: ellipse rx value
  hitboxRy?: number;             // For lone pairs: ellipse ry value
  hoverColor?: string;            // Color for hover highlights
  selectionColor?: string;         // Color for selection highlights
  hoverOpacity?: number;            // Opacity for hover highlights
  selectedOpacity?: number;         // Opacity for selected highlights

  // Selection-specific styling
  selectStroke?: string;          // Stroke color for selected objects
  selectColor?: string;           // Fill color for selected objects
}

export interface FeedbackConfig {
  states: Record<FeedbackState, FeedbackStyle>;
  transitions: Record<string, any>;
  enableAnimations: boolean;
  hoverDelay: number;

  /**
   * Color for selection highlights (default: '#0066ff')
   * NOTE: When changing this color, also update the arrowhead-highlight marker fill in Defs.tsx
   */
  selectionColor: string;
  hoverColor: string;
  errorColor: string;
  successColor: string;
  previewOpacity: number;

  // Universal selection/hover visual settings (apply to all element types)
  /** Opacity for hovered elements (default: 0.4) */
  hoverOpacity: number;
  /** Opacity for selected elements (default: 0.6) */
  selectedOpacity: number;

  hitboxStroke?: string;

  // Hitbox configuration (migrated from HitboxConfig)
  /** Show/hide hitboxes by type */
  showAtomHitboxes: boolean;
  showBondHitboxes: boolean;
  showArrowHitboxes: boolean;

  /** Hitbox colors */
  atomColor: string;
  bondColor: string;
  arrowColor: string;
  lonePairColor: string;

  /** Hitbox sizes */
  atomRadius: number;
  bondThickness: number;
  arrowStrokeMultiplier: number;

  /** Element-specific stroke properties */
  atomStroke?: string;
  atomStrokeWidth?: number;
  bondStroke?: string;
  bondStrokeWidth?: number;
  lonePairStroke?: string;
  lonePairStrokeWidth?: number;
}

export class FeedbackManager {
  private config: FeedbackConfig;
  private elementStates: Map<string, FeedbackState>;
  private elementSubStates: Map<string, Set<string>>; // For partial selections

  constructor(config: Partial<FeedbackConfig> = {}) {
    this.elementStates = new Map();
    this.elementSubStates = new Map();

    this.config = {
      enableAnimations: true,
      hoverDelay: 150,
      selectionColor: '#949494',
      hoverColor: '#90BCD0',
      errorColor: '#ff3333',
      successColor: '#33ff33',
      previewOpacity: 0.5,
      hoverOpacity: 0.4,
      selectedOpacity: 0.6,
      arrowStrokeMultiplier: 2.5,
      // Hitbox defaults (migrated from DEFAULT_HITBOX_CONFIG)
      showAtomHitboxes: true,
      showBondHitboxes: true,
      showArrowHitboxes: true,
      hitboxStroke: '#096D99',
      atomColor: '#3479F6',
      bondColor: '#3479F6',
      lonePairColor: '#90bcd0',
      arrowColor: '#ff6b35',
      atomRadius: 1.6,
      bondThickness: 2.6,
      states: this.createDefaultStates(config),
      transitions: {
        default: { duration: 0.2, ease: 'easeInOut' },
        hover: { duration: 0.15, ease: 'easeOut' },
        selection: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
        error: { duration: 0.3, type: 'spring', stiffness: 500 },
      },
      ...config,
    };
  }

  private createDefaultStates(config: Partial<FeedbackConfig>): Record<FeedbackState, FeedbackStyle> {
    const hoverColor = config.hoverColor || '#90BCD0';
    const errorColor = config.errorColor || '#ff3333';
    const successColor = config.successColor || '#33ff33';
    const hitboxStroke = '#096D99';
    const hoverOpacity = 0.3;

    // Hitbox defaults
    const atomColor = config.atomColor || '#3479F6';
    const bondColor = config.bondColor || '#3479F6';
    const arrowColor = config.arrowColor || '#BE0000';
    const lonePairColor = config.lonePairColor || '#90bcd0';
    const atomRadius = config.atomRadius || 1.6;
    const bondThickness = config.bondThickness || 2.6;
    const arrowStrokeMultiplier = config.arrowStrokeMultiplier || 2.5;

    // Element-specific stroke defaults
    const atomStroke = config.atomStroke || 'black';
    const atomStrokeWidth = config.atomStrokeWidth ?? 0.2;
    const bondStroke = config.bondStroke || 'black';
    const bondStrokeWidth = config.bondStrokeWidth ?? 0.2;
    const lonePairStroke = config.lonePairStroke || 'black';
    const lonePairStrokeWidth = config.lonePairStrokeWidth ?? 0.2;

    return {
      idle: {
        stroke: 'black',
        strokeWidth: 0,
        fill: 'white', // used for charges
        fillOpacity: 0,
        scale: 1,
        opacity: 1,
        cursor: 'pointer',
        atomColor,
        bondColor,
        arrowColor,
        lonePairColor,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        // Idle state: no strokes visible
        atomStroke: 'transparent',
        atomStrokeWidth: 0,
        bondStroke: 'transparent',
        bondStrokeWidth: 0,
        lonePairStroke: 'transparent',
        lonePairStrokeWidth: 0,
      },
      hover: {
        stroke: "black",
        strokeWidth: .2,
        fill: hoverColor,
        fillOpacity: hoverOpacity,
        hitboxStroke,
        scale: 1.02,
        opacity: 1,
        cursor: 'pointer',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
        atomColor: hoverColor,
        bondColor: hoverColor,
        arrowColor: hoverColor,
        lonePairColor: hoverColor,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
      'partial-hover': {
        stroke: "black",
        strokeWidth: .2,
        strokeDasharray: '4 2',
        fill: hoverColor,
        fillOpacity: hoverOpacity,
        scale: 1.01,
        opacity: 1,
        cursor: 'pointer',
        atomColor,
        bondColor,
        arrowColor,
        lonePairColor,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
      selected: {
        stroke: "black",
        strokeWidth: .2,
        fill: hoverColor,
        fillOpacity: 1.0,
        scale: 1.03,
        opacity: hoverOpacity,
        cursor: 'move',
        filter: 'drop-shadow(0 3px 6px rgba(0,102,255,0.3))',
        atomColor: hoverColor,
        bondColor: hoverColor,
        arrowColor: hoverColor,
        lonePairColor: hoverColor,
        hitboxStroke,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
      'selected-hover': {
        stroke: "black",
        strokeWidth: .2,
        fill: hoverColor,
        fillOpacity: 1.0,
        hitboxStroke,
        scale: 1.03,
        opacity: 1,
        cursor: 'move',
        filter: 'drop-shadow(0 4px 8px rgba(0,102,255,0.4))',
        atomColor: hoverColor,
        bondColor: hoverColor,
        arrowColor: hoverColor,
        lonePairColor: hoverColor,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
      'anchor-orbit-hover': {
        stroke: "black",
        strokeWidth: .2,
        fill: hoverColor,
        fillOpacity: hoverOpacity,
        hitboxStroke,
        scale: 1.02,
        opacity: 1,
        cursor: 'none', // Hide cursor - custom cursor will be shown
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
        atomColor: hoverColor,
        bondColor: hoverColor,
        arrowColor: hoverColor,
        lonePairColor: hoverColor,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
      'anchor-orbit-dragging': {
        stroke: "black",
        strokeWidth: .2,
        fill: hoverColor,
        fillOpacity: 1.0,
        hitboxStroke,
        scale: 1.03,
        opacity: 1,
        cursor: 'none', // Hide cursor - custom cursor will be shown
        filter: 'drop-shadow(0 3px 6px rgba(0,102,255,0.3))',
        atomColor: hoverColor,
        bondColor: hoverColor,
        arrowColor: hoverColor,
        lonePairColor: hoverColor,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
      // MC-899 follow-up: visual highlight for atoms being rotated along
      // with the dragged atom. Mirrors `selected` styling so the user can
      // clearly see the rigid set that's about to move.
      'anchor-orbit-orbiting': {
        stroke: "black",
        strokeWidth: .2,
        fill: hoverColor,
        fillOpacity: 1.0,
        scale: 1.03,
        opacity: hoverOpacity,
        cursor: 'none', // anchor-orbit context, custom cursor active
        filter: 'drop-shadow(0 3px 6px rgba(0,102,255,0.3))',
        atomColor: hoverColor,
        bondColor: hoverColor,
        arrowColor: hoverColor,
        lonePairColor: hoverColor,
        hitboxStroke,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
      disabled: {
        stroke: "black",
        strokeWidth: 0,
        fill: 'transparent',
        fillOpacity: 0,
        scale: 1,
        opacity: 0.5,
        cursor: 'not-allowed',
        atomColor,
        bondColor,
        arrowColor,
        lonePairColor,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
      error: {
        stroke: errorColor,
        strokeWidth: 0,
        fill: hoverColor,
        fillOpacity: 0.1,
        hitboxStroke,
        scale: 1.05,
        opacity: 1,
        cursor: 'pointer',
        filter: 'drop-shadow(0 0 4px rgba(255,51,51,0.5))',
        atomColor,
        bondColor,
        arrowColor,
        lonePairColor,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
      success: {
        stroke: successColor,
        strokeWidth: 0,
        fill: successColor,
        fillOpacity: 0.1,
        scale: 1.03,
        opacity: 1,
        cursor: 'pointer',
        filter: 'drop-shadow(0 0 4px rgba(51,255,51,0.5))',
        atomColor,
        bondColor,
        arrowColor,
        lonePairColor,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
      preview: {
        stroke: 'black',
        strokeWidth: 0,
        strokeDasharray: '2 2',
        fill: 'transparent',
        fillOpacity: 0,
        scale: 1,
        opacity: config.previewOpacity || 0.5,
        cursor: 'pointer',
        atomColor,
        bondColor,
        arrowColor,
        lonePairColor,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
      ghost: {
        stroke: '#999999',
        strokeWidth: 0,
        strokeDasharray: '1 3',
        fill: 'transparent',
        fillOpacity: 0,
        scale: 0.98,
        opacity: 0.3,
        cursor: 'grab',
        atomColor,
        bondColor,
        arrowColor,
        lonePairColor,
        atomRadius,
        bondThickness,
        arrowStrokeMultiplier,
        atomStroke,
        atomStrokeWidth,
        bondStroke,
        bondStrokeWidth,
        lonePairStroke,
        lonePairStrokeWidth,
      },
    };
  }

  setState(elementId: string, state: FeedbackState) {
    this.elementStates.set(elementId, state);
  }

  getState(elementId: string): FeedbackState {
    return this.elementStates.get(elementId) || 'idle';
  }

  setPartialState(elementId: string, subElementId: string) {
    if (!this.elementSubStates.has(elementId)) {
      this.elementSubStates.set(elementId, new Set());
    }
    this.elementSubStates.get(elementId)!.add(subElementId);
  }

  clearPartialState(elementId: string) {
    this.elementSubStates.delete(elementId);
  }

  getStyle(state: FeedbackState): FeedbackStyle {
    return this.config.states[state] || this.config.states.idle;
  }

  getTransition(type: string = 'default') {
    return this.config.transitions[type] || this.config.transitions.default;
  }

  getConfig(): FeedbackConfig {
    return this.config;
  }

  createVariants(_elementId: string): Variants {
    const variants: Variants = {};

    Object.entries(this.config.states).forEach(([state, style]) => {
      variants[state] = {
        ...style,
        transition: this.getTransition(state === 'hover' ? 'hover' :
                                       state === 'selected' ? 'selection' :
                                       state === 'error' ? 'error' : 'default'),
      };
    });

    return variants;
  }

  // Compound state management
  getCompoundState(isHovered: boolean, isSelected: boolean, isDisabled: boolean = false): FeedbackState {
    if (isDisabled) return 'disabled';
    if (isSelected && isHovered) return 'selected-hover';
    if (isSelected) return 'selected';
    if (isHovered) return 'hover';
    return 'idle';
  }

  // Advanced effects
  pulseEffect(elementId: string, duration: number = 1000) {
    const originalState = this.getState(elementId);
    this.setState(elementId, 'success');
    setTimeout(() => {
      this.setState(elementId, originalState);
    }, duration);
  }

  shakeEffect(elementId: string) {
    this.setState(elementId, 'error');
    setTimeout(() => {
      this.setState(elementId, 'idle');
    }, 500);
  }
}

/**
 * React Hook for using feedback styles
 *
 * Manages feedback state for a single element and provides controls.
 *
 * @example
 * ```tsx
 * const feedback = useFeedback(atomId);
 *
 * <motion.circle
 *   variants={feedback.variants}
 *   animate={feedback.state}
 *   stroke={feedback.currentStyle.stroke}
 *   onHoverStart={() => feedback.setHovered(true)}
 *   onHoverEnd={() => feedback.setHovered(false)}
 * />
 * ```
 */
export const useFeedback = (elementId: string) => {
  const [manager] = React.useState(() => new FeedbackManager());
  const [state, setState] = React.useState<FeedbackState>('idle');
  const [isHovered, setIsHovered] = React.useState(false);
  const [isSelected, setIsSelected] = React.useState(false);
  const [isDisabled, setIsDisabled] = React.useState(false);

  React.useEffect(() => {
    const newState = manager.getCompoundState(isHovered, isSelected, isDisabled);
    setState(newState);
    manager.setState(elementId, newState);
  }, [isHovered, isSelected, isDisabled, elementId, manager]);

  const variants = useMemo(() => manager.createVariants(elementId), [elementId, manager]);
  const currentStyle = manager.getStyle(state);

  return {
    state,
    variants,
    currentStyle,
    config: manager.getConfig(),
    setHovered: setIsHovered,
    setSelected: setIsSelected,
    setDisabled: setIsDisabled,
    pulse: () => manager.pulseEffect(elementId),
    shake: () => manager.shakeEffect(elementId),
  };
};
